import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import hotToast, { Toaster } from 'react-hot-toast';
import { useToast } from '../lib/hooks/useToast';
import { useConfirm } from '../lib/hooks/useConfirm';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { AuthProvider, useUserData } from '../lib/auth';
import { useNotificationStore } from '../lib/stores/notificationStore';
import { useApiConfigurationStore } from '../lib/stores/apiConfigurationStore';
import { useSettingsStore } from '../lib/stores/settingsStore';
import { TaskCreationProgress } from './projects/TaskCreationProgress';
import { supabase } from '../lib/supabase';
import type { WhatsAppMessage, Notification as AppNotification } from '../lib/types';
import { logger, useLoggerInit } from '../lib/utils/logger';
import { leadsRealtimeService } from '../lib/services/leadsRealtimeService';
import { taskRealtimeService } from '../lib/services/taskRealtimeService';
import { warehouseStockAlertService } from '../lib/services/warehouseStockAlertService';
import { useMenuLabels } from '../lib/hooks/useMenuLabels';
import { autoSyncService } from '../lib/services/autoSyncService';

interface AppContextType {
  toast: ReturnType<typeof useToast>;
  confirm: ReturnType<typeof useConfirm>['confirm'];
  language: string;
  setLanguage: (lang: 'en' | 'it') => void;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

interface AppProviderProps {
  children: React.ReactNode;
}

// Controlla se siamo su una route di un portale esterno (client, partner, collaborator)
// In tal caso, i servizi CRM (realtime, notifiche, WhatsApp) NON devono inizializzarsi
// per evitare interferenze (es. toast auto-notifica, broadcast self su stessa connessione)
const isPortalRoute = () => {
  const path = window.location.pathname;
  return path.startsWith('/client-portal') ||
         path.startsWith('/partner-portal') ||
         path.startsWith('/collaborator-portal');
};

// Componente interno che ha accesso a useUserData
function AppProviderInner({ children }: AppProviderProps) {
  const toast = useToast();
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();
  const { createNotification } = useNotificationStore();
  const { configurations, fetchConfigurations } = useApiConfigurationStore();
  const { settings, fetchSettings, updateSettings } = useSettingsStore();
  const { organizationId, userProfile } = useUserData(); // Usa UserContext da auth.ts
  const { lead: leadLabel } = useMenuLabels();

  // Language state - default to Italian
  const [language, setLanguageState] = useState<'en' | 'it'>('it');

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Initialize logger and language from settings
  useLoggerInit();
  
  useEffect(() => {
    // Fetch settings when the component mounts
    fetchSettings();
  }, [fetchSettings]);
  
  useEffect(() => {
    if (settings?.language) {
      setLanguageState(settings.language);
    }
  }, [settings]);
  
  // Function to update language
  const setLanguage = async (lang: 'en' | 'it') => {
    setLanguageState(lang);
    if (settings) {
      try {
        await updateSettings({ language: lang });
      } catch (error) {
        console.error('Error updating language setting:', error);
      }
    }
  };
  
  // (Rimosso: setUser del vecchio whatsappStore — ora il drawer gestisce il proprio
  // wahaConfig via useWhatsAppChatStore.loadConfig in WhatsAppDrawer.tsx)

  // Ref per toast (evita re-render loop - toast crea nuovo oggetto ogni render)
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // Initialize autoSyncService for WooCommerce auto-sync (solo CRM)
  useEffect(() => {
    if (isPortalRoute()) return;
    const initAutoSync = async () => {
      if (organizationId) {
        try {
          await autoSyncService.initialize(organizationId);
          autoSyncService.setToastCallback((message, type) => {
            toastRef.current[type](message);
          });
          console.log('[AutoSync] Service initialized for organization:', organizationId);
        } catch (error) {
          console.error('[AutoSync] Error initializing service:', error);
        }
      } else {
        // Reset service when user logs out
        autoSyncService.reset();
      }
    };

    initAutoSync();
  }, [organizationId]);

  // Funzione per verificare se l'utente è autenticato
  const isAuthenticated = async () => {
    try {
      const { data } = await supabase.auth.getSession();
      return !!data.session?.user;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  };

  // Carica le configurazioni API solo dopo l'autenticazione (solo CRM)
  useEffect(() => {
    if (isPortalRoute()) return;
    const loadConfigurations = async () => {
      const authenticated = await isAuthenticated();
      if (authenticated) {
        fetchConfigurations();
      }
    };

    loadConfigurations();
  }, [fetchConfigurations]);
  
  // (Rimosso: precaricamento chat WhatsApp via vecchio whatsappStore.
  //  Ora il caricamento delle chat avviene in WhatsAppDrawer.tsx tramite
  //  useWhatsAppChatStore.loadChats() appena la config è disponibile, e il
  //  realtime è gestito da useWhatsAppRealtime hook nel drawer.)
  
  // ⚠️ DISABILITATO: Il vecchio whatsappRealtimeService creava un secondo
  // postgres_changes subscription su whatsapp_messages, conflittuando con
  // useWhatsAppRealtime hook usato da WhatsApp2Page.
  // Due subscriptions sulla stessa tabella dalla stessa connessione Supabase
  // causano la perdita silenziosa di tutti gli eventi INSERT/UPDATE.
  // WhatsApp2Page usa il proprio hook dedicato useWhatsAppRealtime.

  // 🚀 Inizializzazione sistema realtime Leads e Tasks (solo CRM, NON nei portali)
  useEffect(() => {
    if (isPortalRoute()) return;

    const initRealtimeServices = async () => {
      const authenticated = await isAuthenticated();
      if (!authenticated) {
        return;
      }

      // Aspetta che organization_id sia disponibile
      if (!organizationId) {
        return;
      }

      // Inizializza il servizio realtime per i leads
      await leadsRealtimeService.init(organizationId);
      logger.log('realtime', 'Leads Realtime Service inizializzato');

      // Inizializza il servizio realtime per tasks e commenti cliente
      await taskRealtimeService.init(organizationId);
      logger.log('realtime', 'Task Realtime Service inizializzato');

      // Inizializza il servizio avvisi scorta magazzino (Ideariso D14)
      await warehouseStockAlertService.init(organizationId);
      logger.log('realtime', 'Warehouse Stock Alert Service inizializzato');
    };

    initRealtimeServices();

    // Cleanup alla chiusura
    return () => {
      leadsRealtimeService.disconnect();
      taskRealtimeService.disconnect();
      warehouseStockAlertService.disconnect();
    };
  }, [organizationId]);

  // 🔔 Subscription realtime per notifiche (solo CRM)
  useEffect(() => {
    if (isPortalRoute()) return;

    let channel: any = null;

    const setupNotificationsRealtime = async () => {
      const authenticated = await isAuthenticated();
      if (!authenticated) {
        return;
      }

      // Ottieni l'user ID corrente
      const { data } = await supabase.auth.getUser();
      if (!data.user) return;

      const userId = data.user.id;

      // Crea canale per le notifiche
      channel = supabase
        .channel(`notifications_${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          (payload: any) => {
            // Ricarica tutte le notifiche per aggiornare i contatori
            useNotificationStore.getState().fetchNotifications();
          }
        )
        .subscribe();
    };

    setupNotificationsRealtime();

    // Cleanup
    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, []);

  // 🔄 Subscription realtime per settings (sincronizza impostazioni tra utenti)
  useEffect(() => {
    if (isPortalRoute()) return;
    if (!organizationId) return;

    const channel = supabase
      .channel(`settings_${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'settings',
          filter: `organization_id=eq.${organizationId}`,
        },
        () => {
          // Ricarica le impostazioni forzando il bypass della cache
          fetchSettings(true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [organizationId, fetchSettings]);

  // 🔔 Listener per toast nuovo lead (solo CRM)
  useEffect(() => {
    if (isPortalRoute()) return;
    const handleNewLeadToast = (event: CustomEvent<{ leadId: string; leadName: string }>) => {
      const { leadId, leadName } = event.detail;
      console.log('[AppProvider] 🔔 Received new-lead-toast event:', leadId, leadName);

      hotToast.custom(
        (t) => (
          <div
            className={`flex items-center gap-3 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg ${
              t.visible ? 'animate-enter' : 'animate-leave'
            }`}
          >
            <div className="flex-1">
              <div className="font-medium">{leadLabel.nuovo} {leadLabel.singularLower} arrivato</div>
              <div className="text-sm opacity-90">{leadName}</div>
            </div>
            <button
              onClick={() => {
                hotToast.dismiss(t.id);
                if (window.location.pathname === '/leads') {
                  window.dispatchEvent(new CustomEvent('open-lead-detail', { detail: { leadId } }));
                } else {
                  window.location.href = `/leads?openLead=${leadId}`;
                }
              }}
              className="bg-white text-green-600 px-3 py-1 rounded text-sm font-medium hover:bg-green-50 transition-colors"
            >
              Apri
            </button>
            <button
              onClick={() => hotToast.dismiss(t.id)}
              className="text-white/70 hover:text-white"
            >
              ✕
            </button>
          </div>
        ),
        { duration: 10000 }
      );
    };

    window.addEventListener('new-lead-toast', handleNewLeadToast as EventListener);

    return () => {
      window.removeEventListener('new-lead-toast', handleNewLeadToast as EventListener);
    };
  }, []);

  // 🔔 Listener per toast notifiche ad alta priorità (solo CRM)
  useEffect(() => {
    if (isPortalRoute()) return;
    const handleNotificationToast = (event: CustomEvent<{ notification: AppNotification }>) => {
      const { notification } = event.detail;
      if (notification.priority !== 'high' && notification.priority !== 'urgent') return;

      hotToast.custom(
        (t) => (
          <div
            className={`flex items-center gap-3 bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg max-w-md ${
              t.visible ? 'animate-enter' : 'animate-leave'
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{notification.title}</div>
              <div className="text-xs text-blue-100 truncate">{notification.message}</div>
            </div>
            {notification.link && (
              <button
                onClick={() => {
                  hotToast.dismiss(t.id);
                  window.location.href = notification.link!;
                }}
                className="bg-white text-blue-600 px-3 py-1 rounded text-sm font-medium hover:bg-blue-50 transition-colors flex-shrink-0"
              >
                Apri
              </button>
            )}
            <button
              onClick={() => hotToast.dismiss(t.id)}
              className="text-white/70 hover:text-white flex-shrink-0"
            >
              ✕
            </button>
          </div>
        ),
        { duration: 8000 }
      );
    };

    window.addEventListener('notification-received', handleNotificationToast as EventListener);

    return () => {
      window.removeEventListener('notification-received', handleNotificationToast as EventListener);
    };
  }, []);

  const value = {
    toast,
    confirm,
    language,
    setLanguage,
    drawerOpen,
    setDrawerOpen
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 5000,
          className: 'flusia-toast',
          style: {
            fontSize: '12px',
            maxWidth: '520px',
            minWidth: '280px',
            padding: '8px 14px',
          },
        }}
        containerStyle={{ zIndex: 99999 }}
      />
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        confirmVariant={confirmState.confirmVariant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <TaskCreationProgress />
    </AppContext.Provider>
  );
}

// Componente wrapper che fornisce AuthProvider
export function AppProvider({ children }: AppProviderProps) {
  return (
    <AuthProvider>
      <AppProviderInner>
        {children}
      </AppProviderInner>
    </AuthProvider>
  );
}
